import type { EntitySnapshot } from '../../net/protocol';

/** The seven things a model can be doing. */
export type AnimState = 'idle' | 'walk' | 'attack' | 'cast' | 'death' | 'dodge' | 'spin';

/**
 * Picks the animation for an entity from its authoritative snapshot.
 *
 * Pure and separately tested: the mapping is the one place a wrong branch would
 * make a corpse swing a sword, and it needs no WebGL to verify.
 *
 * `hp` is checked before `state` deliberately. The server sets `state` to `DEAD`
 * for dead players, but a snapshot that arrives mid-transition could carry a
 * stale action state with zero HP — health is the fact, state is the label.
 */
export function animStateFor(entity: EntitySnapshot, casting = false): AnimState {
  if (entity.hp <= 0 || entity.state === 'DEAD') return 'death';

  // A dodge outranks the cast overlay, and it is the only thing that does.
  //
  // `DODGING` is a *server* state: while it holds, the simulation is carrying the
  // body along the roll and refusing damage. A cast, by contrast, is a client-side
  // timer over an outcome that has already been applied — so if the overlay won,
  // a player who rolls straight out of a skill would keep casting on screen while
  // the server slid them four metres, and the animation would be describing
  // something the simulation is not doing. It is also the frame that has to read:
  // the roll is the only explanation anyone gets for why a hit missed.
  if (entity.state === 'DODGING') return 'dodge';

  // **A channel outranks the client's own cast timer**, and that ordering is the point. A
  // channel is entered *through* a wind-up, so `casting` is still running from
  // EVENT_SKILL_CAST_STARTED at the moment the spin begins — letting the timer win would draw
  // the wind-up gesture for the whole three seconds of Whirlwind. The server state is the fact,
  // the timer is an overlay over an outcome already applied, and the fact outranks it.
  if (entity.state === 'CHANNELLING') return 'spin';

  // A cast animation is driven by the client's own timer, started by
  // EVENT_SKILL_CAST_STARTED. It outranks walking and the basic attack because it is
  // the thing the player just deliberately did.
  //
  // The timer rather than the state, and for the same reason the roll uses one: it starts on
  // the tick the event arrives. The `CASTING` case below is the fallback for a windowed cast
  // whose start event was missed, which is what a client joining mid-cast sees.
  //
  // **Since Phase 29.2 nearly every skill is windowed.** This comment used to say the 85
  // instant skills had no state at all; they now have a derived wind-up, and only a genuinely
  // `INSTANT` skill resolves in one tick.
  if (casting) return 'cast';
  if (entity.state === 'CASTING') return 'cast';

  switch (entity.state) {
    // WIND_UP and RECOVERY are the two halves of one swing (Phase 29.5) and both map to the
    // attack clip *deliberately*: the split exists for the HUD and for a spectator reading
    // "damage is coming" against "locked out", not for the skeleton. Mapping them to different
    // clips — or leaving RECOVERY to fall through to `idle` — would make every swing visibly
    // restart or snap to a standing pose halfway through its own follow-through.
    case 'WIND_UP':
    case 'RECOVERY':
    case 'ATTACKING':
      return 'attack';
    case 'MOVING':
    case 'CHASING':
      return 'walk';
    default:
      return 'idle';
  }
}

/**
 * Seconds to cross-fade when the animation changes.
 *
 * Death does not blend — it starts from wherever the body was, which is what
 * makes it look like a collapse rather than a transition.
 */
export const BLEND_SECONDS = 0.14;

/** How long the death animation takes to play out, in seconds. */
export const DEATH_SECONDS = 0.9;
